import React, { useEffect, useRef } from "react";
import "./HeroOrbit.css";

// Debe coincidir con la duración de la animación orbitTravel en HeroOrbit.css.
const ORBIT_DURATION = 42;

const PROJECTS = [
  {
    id: "patrimonio-vivo",
    name: "Patrimonio Vivo",
    logo: "/img/logopv.png",
    link: "https://patrimoniovivo.sparklingtech.es/",
    color: "#c084fc",
    delay: 0
  },
  {
    id: "adaia",
    name: "ADAIA",
    logo: "/img/ADAIA_logo_1.png",
    link: "https://adaia.sparklingtech.es/",
    color: "#ec4899",
    delay: -14
  },
  {
    id: "neuroglint",
    name: "NeuroGlint",
    logo: "/img/NeuroglintBlanco_corto.png",
    link: "https://neuroglint.sparklingtech.es/",
    color: "#6366f1",
    delay: -28
  }
];

// Small twinkling "stars" that travel the same orbit line, each in one of the palette
// colors, at its own speed/size/brightness rhythm so they never all shine in sync.
// begin = -(fraction of the loop already travelled at load) * dur, with fractions spaced
// 1/8 apart, so the eight stars start evenly spread around the ellipse instead of bunched
// near the same point.
const TRACK_STARS = [
  { color: "blue", scale: 0.55, dur: 10, begin: 0, twinkleDur: 2.4, twinkleDelay: 0 },
  { color: "purple", scale: 0.4, dur: 13, begin: -1.625, twinkleDur: 3.1, twinkleDelay: -0.8 },
  { color: "pink", scale: 0.62, dur: 16, begin: -4, twinkleDur: 2, twinkleDelay: -1.4 },
  { color: "blue", scale: 0.35, dur: 11, begin: -4.125, twinkleDur: 2.8, twinkleDelay: -2 },
  { color: "pink", scale: 0.46, dur: 14, begin: -7, twinkleDur: 3.4, twinkleDelay: -0.4 },
  { color: "purple", scale: 0.5, dur: 12, begin: -7.5, twinkleDur: 2.2, twinkleDelay: -1.1 },
  { color: "blue", scale: 0.42, dur: 15, begin: -11.25, twinkleDur: 2.6, twinkleDelay: -1.8 },
  { color: "pink", scale: 0.38, dur: 9, begin: -7.875, twinkleDur: 3, twinkleDelay: -0.2 }
];

// Ellipse centered at (220,140), rx=210 ry=95, rotated -20deg — baked into the arc's own
// x-axis-rotation so the exact same "d" drives the visible line, the sparks (SMIL, via
// mpath) and the bubbles (CSS offset-path). One shared definition keeps everything aligned.
const ELLIPSE_D = "M 417.34 68.18 A 210 95 -20 1 1 417.05 67.41 Z";

// Sharp, thin-waisted 4-point sparkle (curved concave sides) — used for the small
// traveling stars, the soft halo and the main star's solid core. Keep the waist control
// points around 4-4.5: much smaller and the four points pinch together at the center and
// stop reading as a star at all.
const STAR_D = "M12 0C12 4.2 4.2 12 0 12C4.2 12 12 19.8 12 24C12 19.8 19.8 12 24 12C19.8 12 12 4.2 12 0Z";

const HeroOrbit = () => {
  const rootRef = useRef(null);
  const svgRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");

    const syncMotion = () => {
      const reduce = mq.matches || document.documentElement.classList.contains("a11y-reduce-motion");
      if (reduce) {
        rootRef.current?.classList.add("is-static");
        svgRef.current?.pauseAnimations?.();
      } else {
        rootRef.current?.classList.remove("is-static");
        svgRef.current?.unpauseAnimations?.();
      }
    };

    syncMotion();
    mq.addEventListener("change", syncMotion);
    window.addEventListener("a11y:reduce-motion", syncMotion);

    return () => {
      mq.removeEventListener("change", syncMotion);
      window.removeEventListener("a11y:reduce-motion", syncMotion);
    };
  }, []);

  return (
    <div className="hero-orbit" ref={rootRef}>
      <div className="hero-orbit-scaler">
        <svg
          ref={svgRef}
          className="hero-orbit-svg"
          width="440"
          height="280"
          viewBox="0 0 440 280"
          aria-hidden="true"
          focusable="false"
        >
          <defs>
            <linearGradient id="hero-ellipse-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#6366f1" />
              <stop offset="30%" stopColor="#818cf8" />
              <stop offset="55%" stopColor="#c084fc" />
              <stop offset="80%" stopColor="#ec4899" />
              <stop offset="100%" stopColor="#6366f1" />
            </linearGradient>
          </defs>

          <path id="hero-ellipse" className="hero-ellipse-path" d={ELLIPSE_D} />

          {/* Twinkling colored stars traveling the orbit — never pause on hover */}
          {TRACK_STARS.map((star, index) => (
            <g
              key={index}
              className={`hero-track-star hero-track-star--${star.color}`}
              style={{ animationDuration: `${star.twinkleDur}s`, animationDelay: `${star.twinkleDelay}s` }}
            >
              <animateMotion dur={`${star.dur}s`} begin={`${star.begin}s`} repeatCount="indefinite" rotate="auto">
                <mpath href="#hero-ellipse" xlinkHref="#hero-ellipse" />
              </animateMotion>
              <path d={STAR_D} transform={`scale(${star.scale}) translate(-12,-12)`} />
            </g>
          ))}

        </svg>

        {PROJECTS.map((project) => {
          // Posición estática equivalente a su fase en la órbita (misma matemática que
          // el animation-delay negativo), usada cuando la animación se congela: si no,
          // las tres burbujas caerían todas en el mismo punto de partida y quedarían
          // apiladas.
          const restOffsetPercent = ((-project.delay % ORBIT_DURATION) / ORBIT_DURATION) * 100;
          return (
          <a
            key={project.id}
            href={project.link}
            target="_blank"
            rel="noopener noreferrer"
            className="hero-bubble-wrap"
            style={{
              "--bubble-color": project.color,
              "--rest-offset": `${restOffsetPercent}%`,
              animationDelay: `${project.delay}s`,
            }}
            aria-label={`Ir a ${project.name} (se abre en una pestaña nueva)`}
          >
            <span className="hero-bubble">
              <span className="hero-bubble-glow" aria-hidden="true" />
              <span className="hero-bubble-glass">
                <span className="hero-bubble-rim" aria-hidden="true" />
                <img src={project.logo} alt={project.name} />
              </span>
            </span>
          </a>
          );
        })}
      </div>
    </div>
  );
};

export default HeroOrbit;
